import type { UrlType } from './types'

export const isValidWalletAddress = (addr: string): boolean =>
  /^0x[a-fA-F0-9]{40}$/.test(String(addr || '').trim())

export const isValidEmail = (email: string): boolean => {
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
  return re.test(String(email).toLowerCase())
}

export const isValidTelegramHandleOrLink = (value: string): boolean => {
  const val = value.trim()
  if (!val) return true
  if (val.startsWith('@')) return val.length > 1
  if (val.startsWith('https://t.me/') || val.startsWith('https://telegram.me/'))
    return val.length > 13
  return false
}

export const isValidHttpsUrl = (url: string): boolean => {
  if (!url || !url.trim()) return true
  if (!url.startsWith('https://')) return false
  try {
    new URL(url)
    return true
  } catch {
    return false
  }
}

export const isValidTwitterUrl = (url: string): boolean => {
  if (!url || !url.trim()) return true
  if (!url.startsWith('https://')) return false
  try {
    const urlObj = new URL(url)
    const host = urlObj.hostname.toLowerCase().replace(/^www\./, '')
    return host === 'x.com' || host === 'twitter.com'
  } catch {
    return false
  }
}

export const isValidDiscordUrl = (url: string): boolean => {
  if (!url || !url.trim()) return true
  if (!url.startsWith('https://')) return false
  try {
    const urlObj = new URL(url)
    const host = urlObj.hostname.toLowerCase().replace(/^www\./, '')
    return host === 'discord.gg' || host === 'discord.com'
  } catch {
    return false
  }
}

export const isValidTelegramUrl = (url: string): boolean => {
  if (!url || !url.trim()) return true
  if (!url.startsWith('https://')) return false
  try {
    const urlObj = new URL(url)
    const host = urlObj.hostname.toLowerCase().replace(/^www\./, '')
    return host === 't.me' || host === 'telegram.me' || host === 'telegram.org'
  } catch {
    return false
  }
}

export const normalizeNumberString = (value: string | number): string =>
  String(value ?? '')
    .replace(/[^0-9.]/g, '')
    .trim()

export const percentToBips = (pct: number): number => Math.floor(pct * 100)

export const validateHttpsUrl = (url: string): string => {
  if (!url.trim()) return 'Enter a valid URL (https).'
  if (!url.startsWith('https://')) return 'Enter a valid URL (https).'
  try {
    new URL(url)
    return ''
  } catch {
    return 'Enter a valid URL (https).'
  }
}

export const getUrlErrorMessage = (url: string, type: UrlType): string => {
  if (!url.trim()) {
    if (type === 'website') return 'Official Site URL is required'
    if (type === 'x') return 'X (Twitter) URL is required'
    return ''
  }
  if (url.length > 2000) return 'This URL is too long.'
  if (!url.startsWith('https://')) return 'Enter a valid URL (https).'
  try {
    const urlObj = new URL(url)
    const host = urlObj.hostname.toLowerCase().replace(/^www\./, '')
    switch (type) {
      case 'x':
        return host === 'x.com' || host === 'twitter.com'
          ? ''
          : 'Enter a valid X (Twitter) URL.'
      case 'discord':
        return host === 'discord.gg' || host === 'discord.com'
          ? ''
          : 'Enter a valid Discord URL.'
      case 'youtube':
        return host === 'youtube.com' || host === 'youtu.be'
          ? ''
          : 'Enter a valid YouTube URL.'
      case 'tiktok':
        return host === 'tiktok.com' ? '' : 'Enter a valid TikTok URL.'
      case 'facebook':
        return host === 'facebook.com' ? '' : 'Enter a valid Facebook URL.'
      case 'instagram':
        return host === 'instagram.com' ? '' : 'Enter a valid Instagram URL.'
      case 'reddit':
        return host === 'reddit.com' ? '' : 'Enter a valid Reddit URL.'
      case 'telegram':
        return host === 't.me' ||
          host === 'telegram.me' ||
          host === 'telegram.org'
          ? ''
          : 'Enter a valid Telegram URL.'
      default:
        return ''
    }
  } catch {
    return 'Enter a valid URL (https).'
  }
}

export const getCharacterCounterColor = (current: number, limit: number) => {
  const percentage = (current / limit) * 100
  if (current > limit) return 'text-status-error'
  if (percentage >= 90) return 'text-status-warning'
  if (percentage >= 50) return 'text-neutral-600'
  return 'text-neutral-400'
}

export const getCharacterHelperText = (current: number, limit: number) => {
  const percentage = (current / limit) * 100
  if (current > limit) return null
  if (percentage >= 90) {
    const remaining = limit - current
    return `${remaining} character${remaining === 1 ? '' : 's'} remaining`
  }
  return null
}

export const getCharacterLengthError = (
  current: number,
  limit: number,
  fieldName: string,
) => {
  if (current <= limit) return null
  const over = current - limit
  const fieldLabel = fieldName === 'projectName' ? 'Name' : 'One-liner'
  return `${fieldLabel} is too long. Limit is ${limit} characters (you're over by ${over}).`
}
